import { WizardLookupEnhancement } from './admissions-lookup';
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, GraduationCap, RotateCcw } from 'lucide-react';
import type {
  AdmissionContext,
  AdmissionField,
  AdmissionFlowReply,
  AdmissionReference,
  AdmissionVersionsReply,
  AdmissionIntent,
} from '@ulaw/content-types/admissions';
import { endpoint } from './content';
import './css/wizard.css';
import { WizardAdvice } from './wizard-advice';

const PdfReferenceReader = lazy(() =>
  import('./reader').then((module) => ({ default: module.PdfReader }))
);

const initialContext: AdmissionContext = {
  admissionYear: 2027,
  versionId: null,
  intent: 'information',
  degreeGoal: 'first_degree',
  entryQualification: 'thpt',
  studyMode: 'regular',
  campusId: 'LPS',
  roundId: 'main',
  interestedMajorIds: [],
};
const intents: { value: AdmissionIntent; label: string; detail: string }[] = [
  {
    value: 'compare',
    label: 'Đối chiếu điểm',
    detail: 'Xem điểm chuẩn và phần điều kiện đã có căn cứ',
  },
  {
    value: 'eligibility',
    label: 'Kiểm tra điều kiện',
    detail: 'Làm rõ điều kiện và thông tin còn thiếu',
  },
  {
    value: 'tuition',
    label: 'Học phí và nơi học',
    detail: 'Tìm thông tin theo khóa và chương trình',
  },
  { value: 'procedure', label: 'Hồ sơ và thủ tục', detail: 'Tìm hiểu hồ sơ và các mốc thời gian' },
  {
    value: 'information',
    label: 'Tôi chưa rõ nên bắt đầu từ đâu',
    detail: 'Tìm hiểu các hướng học phù hợp với nhu cầu',
  },
];
export function WizardEntry() {
  return (
    <Link to="/wizard" className="consultation-entry wizard-entry">
      <GraduationCap size={28} aria-hidden="true" />
      <span>
        <strong>Tìm phương án xét tuyển</strong>
        <small>Chọn nhu cầu, tìm thông tin theo từng năm</small>
      </span>
      <ChevronRight size={20} aria-hidden="true" />
    </Link>
  );
}
export async function wizardRequest<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timer = setTimeout(abort, 25_000);
  try {
    const response = await fetch(endpoint(path), {
      method: body === undefined ? 'GET' : 'POST',
      cache: 'no-store',
      signal: controller.signal,
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
    const data = await response.json();
    if (!response.ok || data.error)
      throw new Error(data.error?.message || 'Chưa thể tải thông tin. Vui lòng thử lại.');
    return data as T;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}
export function parseAdmissionField(
  field: AdmissionField,
  raw: unknown
): { value?: unknown; error?: string } {
  const blank = raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0);
  if (blank) return field.required ? { error: 'Vui lòng bổ sung thông tin này.' } : {};
  if (field.kind === 'number') {
    const text = String(raw).trim().replace(',', '.');
    if (!/^-?\d+(\.\d+)?$/.test(text)) return { error: 'Nhập một số hợp lệ, ví dụ 8,25.' };
    const value = Number(text);
    if (
      !Number.isFinite(value) ||
      (field.min !== undefined && value < field.min) ||
      (field.max !== undefined && value > field.max)
    )
      return {
        error:
          `Giá trị cần ${field.min === undefined ? '' : `từ ${field.min}`} ${field.max === undefined ? '' : `đến ${field.max}`}.`.trim(),
      };
    return { value };
  }
  if (field.kind === 'select' && !field.options?.some((option) => option.value === String(raw)))
    return { error: 'Vui lòng chọn một lựa chọn trong danh sách.' };
  if (
    field.kind === 'multi_select' &&
    (!Array.isArray(raw) ||
      raw.some((value) => !field.options?.some((option) => option.value === value)))
  )
    return { error: 'Lựa chọn chưa hợp lệ.' };
  if (
    field.kind === 'date' &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(String(raw)) ||
      !Number.isFinite(Date.parse(String(raw))) ||
      new Date(String(raw)).toISOString().slice(0, 10) !== raw)
  )
    return { error: 'Ngày chưa hợp lệ.' };
  if (field.valueType === 'number') {
    const value = Number(raw);
    return Number.isFinite(value) ? { value } : { error: 'Lựa chọn chưa hợp lệ.' };
  }
  return { value: typeof raw === 'string' ? raw.trim() : raw };
}

/** UI field paths are transport keys, never executable expressions. */
export function admissionWireAnswers(raw: Record<string, unknown>): Record<string, unknown> {
  const output: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (['__proto__', 'constructor', 'prototype'].some((part) => key.split('.').includes(part)))
      throw new Error('Invalid field path');
    const path = /^([A-Za-z][A-Za-z0-9_]*)\.(\d+)$/.exec(key);
    if (path) {
      const index = Number(path[2]);
      if (index > 100) throw new Error('Invalid field index');
      const items = Array.isArray(output[path[1]]) ? (output[path[1]] as unknown[]) : [];
      items[index] = value;
      output[path[1]] = items;
    } else if (/^[A-Za-z][A-Za-z0-9_]*$/.test(key)) output[key] = value;
    else throw new Error('Invalid field path');
  }
  return output;
}

function AdmissionReferenceItem({ reference }: { reference: AdmissionReference }) {
  const [open, setOpen] = useState(false);
  let safe = false,
    pdf = false;
  try {
    const url = new URL(reference.url);
    safe = url.protocol === 'https:' && !url.username && !url.password;
    pdf = /\.pdf$/i.test(url.pathname);
  } catch {
    /* Invalid source URLs remain text. */
  }
  return (
    <details>
      <summary>{reference.title}</summary>
      <p>
        {reference.locator}
        {reference.pages.length > 0 && ` · Trang ${reference.pages.join(', ')}`}
      </p>
      {safe && (
        <a href={reference.url} target="_blank" rel="noopener noreferrer">
          Mở nguồn chính thức ↗
        </a>
      )}
      {pdf && (
        <>
          <button type="button" className="wizard-secondary" onClick={() => setOpen(!open)}>
            {open
              ? 'Đóng tài liệu'
              : `Xem bản tham chiếu${reference.pages.length ? ` · Trang ${reference.pages[0]}` : ''}`}
          </button>
          {open && (
            <Suspense fallback={<p role="status">Đang mở tài liệu…</p>}>
              <PdfReferenceReader
                url={reference.url}
                reference={{
                  documentId: reference.documentId,
                  sourceSha256: reference.sourceSha256,
                  page: reference.pages[0],
                }}
              />
            </Suspense>
          )}
        </>
      )}
    </details>
  );
}
export function AdmissionReferences({ references }: { references: AdmissionReference[] }) {
  if (!references.length) return null;
  return (
    <section className="wizard-sources" aria-label="Căn cứ nguồn">
      <h3>Căn cứ thông tin</h3>
      {references.map((reference, index) => (
        <AdmissionReferenceItem reference={reference} key={`${reference.documentId}-${index}`} />
      ))}
    </section>
  );
}

function Field({
  field,
  value,
  error,
  change,
}: {
  field: AdmissionField;
  value: unknown;
  error?: string;
  change: (value: unknown) => void;
}) {
  const id = `wizard-field-${field.id}`;
  const description =
    [field.help ? `${id}-help` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') ||
    undefined;
  const props = {
    id,
    'aria-describedby': description,
    'aria-invalid': !!error,
    required: field.required,
  };
  return (
    <div className="wizard-field">
      {field.kind === 'multi_select' ? (
        <fieldset aria-describedby={description} aria-invalid={!!error}>
          <legend>
            {field.label}
            {field.required ? ' *' : ' (không bắt buộc)'}
          </legend>
          {field.options?.map((option) => (
            <label className="wizard-option" key={option.value}>
              <input
                type="checkbox"
                checked={Array.isArray(value) && value.includes(option.value)}
                onChange={(e) =>
                  change(
                    e.target.checked
                      ? [...(Array.isArray(value) ? value : []), option.value]
                      : (Array.isArray(value) ? value : []).filter((item) => item !== option.value)
                  )
                }
              />
              {option.label}
            </label>
          ))}
        </fieldset>
      ) : (
        <>
          <label htmlFor={id}>
            {field.label}
            {field.required ? ' *' : ' (không bắt buộc)'}
          </label>
          {field.kind === 'select' ? (
            <select {...props} value={String(value ?? '')} onChange={(e) => change(e.target.value)}>
              <option value="">Chọn thông tin</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              {...props}
              type={field.kind === 'date' ? 'date' : 'text'}
              inputMode={field.kind === 'number' ? 'decimal' : undefined}
              autoComplete="off"
              value={String(value ?? '')}
              onChange={(e) => change(e.target.value)}
            />
          )}
        </>
      )}
      {field.help && (
        <p id={`${id}-help`} className="muted">
          {field.help}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="wizard-error">
          {error}
        </p>
      )}
    </div>
  );
}

const issueLabels: Record<string, string> = {
  'G26-LAW': 'Chưa xác minh đầy đủ văn bản ngưỡng lĩnh vực pháp luật',
  'G26-REG': 'Quy chế tuyển sinh của Trường năm 2026 cần bổ sung',
  'G26-LOGIC': 'Một số điều kiện đầu vào năm 2026 cần làm rõ',
  'G25-REG': 'Quy chế tuyển sinh của Trường năm 2025 cần bổ sung',
  'G24-REG': 'Quy chế tuyển sinh của Trường năm 2024 cần bổ sung',
  'GX-STANDARD': 'Phạm vi áp dụng chuẩn chương trình cần xác minh',
  'GX-OFFICIAL': 'Chưa có kết quả tuyển sinh cá nhân chính thức',
  'G26-GRADE': 'Cách tính học bạ giữa các nguồn cần được Trường làm rõ',
};
function sourceIssueLabel(issue: string): string {
  return (
    issueLabels[issue] ||
    (/^[A-Z0-9_-]+$/.test(issue) ? 'Một điều kiện hoặc nguồn áp dụng còn chờ xác minh.' : issue)
  );
}

type Snapshot = { reply: AdmissionFlowReply; answers: Record<string, unknown> };
export function AdmissionsWizard() {
  const [versions, setVersions] = useState<AdmissionVersionsReply>();
  const [context, setContext] = useState<AdmissionContext>({ ...initialContext });
  const [stage, setStage] = useState<'context' | 'majors' | 'flow'>('context');
  const [reply, setReply] = useState<AdmissionFlowReply>();
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const active = useRef<AbortController>();
  const requestSequence = useRef(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const lastRequest = useRef<Record<string, unknown>>({});
  const routingValues = useRef<Record<string, unknown>>({});
  const version = versions?.versions.find((item) => item.year === context.admissionYear);
  useEffect(() => {
    void loadVersions();
    return () => {
      active.current?.abort();
    };
  }, []);
  useEffect(() => {
    heading.current?.focus();
  }, [stage, reply]);
  useEffect(() => {
    if (!dirty) return;
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const leave = (event: MouseEvent) => {
      const element =
        event.target instanceof Element ? event.target.closest('a, .miniapp-header button') : null;
      if (
        !element ||
        element.closest('.wizard-sources') ||
        (element instanceof HTMLAnchorElement && element.target === '_blank')
      )
        return;
      if (
        !window.confirm(
          'Thông tin đang nhập chỉ nằm trong bộ nhớ. Rời trang sẽ mất nội dung này. Bạn muốn rời trang?'
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', leave, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      document.removeEventListener('click', leave, true);
    };
  }, [dirty]);
  async function loadVersions() {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    const sequence = ++requestSequence.current;
    setBusy(true);
    setError('');
    try {
      const data = await wizardRequest<AdmissionVersionsReply>(
        '/api/admissions/versions',
        undefined,
        controller.signal
      );
      if (sequence === requestSequence.current) setVersions(data);
    } catch {
      if (!controller.signal.aborted && sequence === requestSequence.current)
        setError('Chưa tải được danh mục năm tuyển sinh. Vui lòng thử lại.');
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }
  async function evaluate(nextAnswers = answers, remember = true) {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    const sequence = ++requestSequence.current;
    setBusy(true);
    setError('');
    setErrors({});
    lastRequest.current = nextAnswers;
    try {
      const result = await wizardRequest<AdmissionFlowReply>(
        '/api/admissions/flow',
        { context, answers: admissionWireAnswers(nextAnswers) },
        controller.signal
      );
      if (sequence !== requestSequence.current) return;
      if (remember && reply) setHistory((previous) => [...previous, { reply, answers }]);
      if (result.action.type === 'ask') {
        const fields = result.action.step.fields;
        setFieldLabels((previous) => ({
          ...previous,
          ...Object.fromEntries(fields.map((field) => [field.id, field.label])),
        }));
      }
      routingValues.current = {
        pathway: nextAnswers.pathway,
        cutoffIndex: nextAnswers.cutoffIndex,
      };
      setAnswers(nextAnswers);
      setContext(result.context);
      setReply(result);
      setStage('flow');
    } catch {
      if (!controller.signal.aborted && sequence === requestSequence.current)
        setError('Chưa thể nhận bước tiếp theo. Nội dung đang nhập vẫn còn; bạn có thể thử lại.');
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }
  function submitStep(event: FormEvent) {
    event.preventDefault();
    if (reply?.action.type !== 'ask') return;
    const next = { ...answers },
      failures: Record<string, string> = {};
    for (const field of reply.action.step.fields) {
      const parsed = parseAdmissionField(field, answers[field.id]);
      if (parsed.error) failures[field.id] = parsed.error;
      else if (parsed.value === undefined) delete next[field.id];
      else next[field.id] = parsed.value;
    }
    const routingChanged = reply.action.step.fields.some(
      (field) =>
        (field.id === 'cutoffIndex' || field.id === 'pathway') &&
        routingValues.current[field.id] !== undefined &&
        next[field.id] !== routingValues.current[field.id]
    );
    if (routingChanged) {
      if (reply.action.step.fields.some((field) => field.id === 'pathway')) delete next.cutoffIndex;
      for (const key of Object.keys(next))
        if (key === 'subjectScores' || key.startsWith('subjectScores.') || key === 'examYear')
          delete next[key];
    }
    setErrors(failures);
    if (Object.keys(failures).length) {
      document.getElementById(`wizard-field-${Object.keys(failures)[0]}`)?.focus();
      return;
    }
    void evaluate(next);
  }
  function reset() {
    if (dirty && !window.confirm('Bắt đầu lại sẽ xóa thông tin đang nhập trong bộ nhớ. Tiếp tục?'))
      return;
    active.current?.abort();
    ++requestSequence.current;
    setBusy(false);
    routingValues.current = {};
    setContext({ ...initialContext });
    setAnswers({});
    setFieldLabels({});
    setReply(undefined);
    setHistory([]);
    setStage('context');
    setError('');
    setErrors({});
    setDirty(false);
  }
  function back() {
    setError('');
    setErrors({});
    if (stage === 'majors') setStage('context');
    else if (history.length) {
      const previous = history[history.length - 1];
      setReply(previous.reply);
      setAnswers((current) => ({ ...previous.answers, ...current }));
      setHistory(history.slice(0, -1));
    } else {
      setStage('majors');
      setReply(undefined);
    }
  }
  const changeContext = <K extends keyof AdmissionContext>(key: K, value: AdmissionContext[K]) => {
    routingValues.current = {};
    setAnswers({});
    setFieldLabels({});
    setHistory([]);
    setReply(undefined);
    setContext((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'admissionYear' ? { versionId: null, interestedMajorIds: [] } : {}),
    }));
    setDirty(true);
  };
  const action = reply?.action;
  return (
    <main className="wizard" aria-busy={busy}>
      <div className="wizard-toolbar">
        {stage !== 'context' && (
          <button type="button" onClick={back} disabled={busy} aria-label="Quay lại bước trước">
            <ArrowLeft size={19} />
            Quay lại
          </button>
        )}
        <button type="button" onClick={reset} disabled={busy}>
          <RotateCcw size={16} />
          Bắt đầu lại
        </button>
      </div>
      <p className="wizard-eyebrow">TƯ VẤN TUYỂN SINH</p>
      <h1 ref={heading} tabIndex={-1}>
        {stage === 'context'
          ? 'Bạn cần hỗ trợ gì?'
          : stage === 'majors'
            ? 'Ngành bạn quan tâm'
            : action?.type === 'ask'
              ? action.step.title
              : action?.type === 'answer'
                ? action.content.title
                : action?.title || 'Thông tin tuyển sinh'}
      </h1>
      <p className="wizard-memory">
        Thông tin chỉ giữ trong bộ nhớ; tải lại hoặc rời trang sẽ mất nội dung đang nhập.
      </p>
      {stage !== 'context' && (
        <p className="wizard-scope">
          {context.admissionYear} ·{' '}
          {context.admissionYear === 2027 ? 'Chuẩn bị tuyển sinh' : 'Tham khảo theo năm'}{' '}
          ·{' '}
          {context.studyMode === 'regular'
            ? 'Chính quy'
            : context.studyMode === 'part_time'
              ? 'Vừa làm vừa học'
              : 'Từ xa'}
          {context.campusId
            ? ` · ${context.campusId === 'LPS' ? 'TP. Hồ Chí Minh (LPS)' : context.campusId === 'LPQ' ? 'Quảng Trị (LPQ)' : context.campusId}`
            : ''}
          {context.roundId
            ? ` · ${context.roundId === 'main' ? 'Đợt chính' : context.roundId === 'supplementary' ? 'Đợt bổ sung' : context.roundId}`
            : ''}
        </p>
      )}
      {reply?.mode === 'isolated_preview' && (
        <p className="wizard-notice" role="note">
          Bản thử nghiệm cô lập · Dữ liệu mô phỏng đã duyệt cho kiểm thử, không dùng làm kết quả
          tuyển sinh.
        </p>
      )}
      {busy && (
        <p role="status" className="wizard-notice">
          Đang tải thông tin…
        </p>
      )}
      {error && (
        <div role="alert" className="wizard-notice wizard-error">
          <p>{error}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              stage === 'context' ? void loadVersions() : void evaluate(lastRequest.current)
            }
          >
            Thử lại
          </button>
        </div>
      )}
      {stage === 'context' && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (version) {
              setContext((previous) => ({ ...previous, versionId: version.id }));
              setStage('majors');
              setDirty(true);
            }
          }}
        >
          <fieldset className="wizard-intents">
            <legend>Nội dung bạn muốn tìm hiểu</legend>
            {intents.map((intent) => (
              <label
                key={intent.value}
                className={`wizard-option ${context.intent === intent.value ? 'chosen' : ''}`}
              >
                <input
                  type="radio"
                  name="intent"
                  value={intent.value}
                  checked={context.intent === intent.value}
                  onChange={() => changeContext('intent', intent.value)}
                />
                <span>
                  <strong>{intent.label}</strong>
                  <small>{intent.detail}</small>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="wizard-field">
            <label htmlFor="wizard-year">Năm tuyển sinh</label>
            <select
              id="wizard-year"
              value={context.admissionYear}
              onChange={(event) => {
                const year = Number(event.target.value) as AdmissionContext['admissionYear'];
                if (
                  Object.keys(answers).length &&
                  !window.confirm('Đổi năm sẽ xóa câu trả lời của phiên bản trước. Tiếp tục?')
                )
                  return;
                changeContext('admissionYear', year);
                setAnswers({});
                setHistory([]);
                setReply(undefined);
              }}
            >
              <option value={2027}>2027 · Chuẩn bị</option>
              {[2026, 2025, 2024].map((year) => (
                <option key={year} value={year}>
                  {year} · Tra cứu lịch sử
                </option>
              ))}
            </select>
          </div>
          <div className="wizard-field">
            <label htmlFor="wizard-degree">Mục tiêu văn bằng</label>
            <select
              id="wizard-degree"
              value={context.degreeGoal}
              onChange={(event) =>
                changeContext('degreeGoal', event.target.value as AdmissionContext['degreeGoal'])
              }
            >
              <option value="first_degree">Bằng đại học đầu tiên</option>
              <option value="second_degree">Bằng đại học thứ hai</option>
              <option value="postgraduate">Sau đại học</option>
            </select>
          </div>
          <div className="wizard-field">
            <label htmlFor="wizard-qualification">Văn bằng đã có</label>
            <select
              id="wizard-qualification"
              value={context.entryQualification}
              onChange={(event) =>
                changeContext(
                  'entryQualification',
                  event.target.value as AdmissionContext['entryQualification']
                )
              }
            >
              <option value="thpt">THPT / đang học THPT</option>
              <option value="college">Cao đẳng</option>
              <option value="university">Đại học</option>
              <option value="other">Khác / văn bằng nước ngoài</option>
            </select>
          </div>
          <div className="wizard-field">
            <label htmlFor="wizard-campus">Nơi học quan tâm</label>
            <select
              id="wizard-campus"
              value={context.campusId || ''}
              onChange={(event) => changeContext('campusId', event.target.value || undefined)}
            >
              <option value="">Chưa rõ nơi học</option>
              <option value="LPS">Trụ sở chính · TP. Hồ Chí Minh</option>
              <option value="LPQ">Phân hiệu Quảng Trị</option>
            </select>
          </div>
          <div className="wizard-field">
            <label htmlFor="wizard-round">Đợt muốn tra cứu</label>
            <select
              id="wizard-round"
              value={context.roundId || ''}
              onChange={(event) => changeContext('roundId', event.target.value || undefined)}
            >
              <option value="">Chưa xác định đợt</option>
              <option value="main">Đợt chính</option>
              <option value="supplementary">Đợt bổ sung</option>
            </select>
          </div>
          <div className="wizard-field">
            <label htmlFor="wizard-mode">Hình thức đào tạo</label>
            <select
              id="wizard-mode"
              value={context.studyMode}
              onChange={(event) =>
                changeContext('studyMode', event.target.value as AdmissionContext['studyMode'])
              }
            >
              <option value="regular">Chính quy</option>
              <option value="part_time">Vừa làm vừa học</option>
              <option value="distance">Từ xa</option>
            </select>
          </div>
          <p className="wizard-notice">
            {context.admissionYear === 2027
              ? 'Thông tin 2027 đang được chuẩn bị. Những phần chưa đủ nguồn sẽ được nêu rõ.'
              : 'Thông tin của mùa tuyển sinh đã qua, dùng để tham khảo lịch sử.'}
          </p>
          {!busy && versions && !version && <p role="alert">Chưa có phiên bản cho năm này.</p>}
          <button className="wizard-primary" disabled={busy || !version}>
            Tiếp tục
          </button>
        </form>
      )}
      {stage === 'majors' && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void evaluate();
          }}
        >
          <p>Có thể chọn nhiều ngành hoặc bỏ qua. Bạn có thể quay lại thay đổi sau.</p>
          <fieldset>
            <legend>Ngành quan tâm (không bắt buộc)</legend>
            {version?.majors.map((major) => (
              <label key={major.value} className="wizard-option">
                <input
                  type="checkbox"
                  checked={context.interestedMajorIds.includes(major.value)}
                  onChange={(event) =>
                    changeContext(
                      'interestedMajorIds',
                      event.target.checked
                        ? [...context.interestedMajorIds, major.value]
                        : context.interestedMajorIds.filter((value) => value !== major.value)
                    )
                  }
                />
                {major.label}
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            className="wizard-secondary"
            disabled={busy}
            onClick={() => changeContext('interestedMajorIds', [])}
          >
            Tôi chưa rõ ngành
          </button>
          <button className="wizard-primary" disabled={busy}>
            Tiếp tục
          </button>
        </form>
      )}
      {stage === 'flow' && action && (
        <div className="wizard-response" hidden={busy}>
          {action.type === 'ask' && (
            <form noValidate onSubmit={submitStep}>
              <p>{action.step.description}</p>
              {action.step.fields.map((field) => (
                <Field
                  key={field.id}
                  field={field}
                  value={answers[field.id]}
                  error={errors[field.id]}
                  change={(value) => {
                    setAnswers((previous) => ({ ...previous, [field.id]: value }));
                    setErrors((previous) => {
                      const next = { ...previous };
                      delete next[field.id];
                      return next;
                    });
                    setDirty(true);
                  }}
                />
              ))}
              <button className="wizard-primary">Tiếp tục</button>
            </form>
          )}
          {action.type === 'answer' && (
            <>
              <div className="wizard-prose">
                {action.content.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <AdmissionReferences references={action.content.references} />
            </>
          )}
          {action.type === 'result' && (
            <>
              {action.options.map((option) => (
                <section key={option.optionId} className="wizard-result">
                  <h2>{option.title}</h2>
                  <p className="wizard-result-status">
                    {option.outcome === 'compared'
                      ? 'Đã đối chiếu bảng chuẩn'
                      : option.outcome === 'condition_not_met'
                        ? 'Chưa đạt điều kiện đang kiểm tra'
                        : option.outcome === 'partial'
                          ? 'Kết quả một phần'
                          : 'Chưa đủ căn cứ kết luận'}
                  </p>
                  {option.score !== undefined && (
                    <p>
                      Điểm tự khai:{' '}
                      <strong>
                        {option.score.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}
                      </strong>
                    </p>
                  )}
                  {option.cutoff !== undefined && (
                    <p>
                      Điểm chuẩn tham khảo: <strong>{option.cutoff.toLocaleString('vi-VN')}</strong>
                    </p>
                  )}
                  {option.difference !== undefined && (
                    <p>
                      Chênh lệch so với chuẩn: {option.difference > 0 ? '+' : ''}
                      {option.difference.toLocaleString('vi-VN')}
                    </p>
                  )}
                  <p className="wizard-notice">
                    Đối chiếu này chưa xác nhận đủ mọi điều kiện hoặc trúng tuyển.
                  </p>
                  {option.checked.length > 0 && (
                    <>
                      <h3>Đã kiểm tra</h3>
                      <ul>
                        {option.checked.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {option.missingAnswers.length > 0 && (
                    <>
                      <h3>Thông tin còn thiếu</h3>
                      <ul>
                        {option.missingAnswers.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {option.sourceIssues.length > 0 && (
                    <>
                      <h3>Nguồn cần xác minh</h3>
                      <ul>
                        {option.sourceIssues.map((item) => (
                          <li key={item}>{sourceIssueLabel(item)}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  <AdmissionReferences references={option.references} />
                </section>
              ))}
            </>
          )}
          {(action.type === 'wait' || action.type === 'handoff') && (
            <>
              <p className="wizard-notice">{action.reason}</p>
              <WizardLookupEnhancement key={context.admissionYear} year={context.admissionYear}/>
              {action.sourceIssues.length > 0 && (
                <ul>
                  {action.sourceIssues
                    .filter((item) => !/^[A-Z0-9_-]+$/.test(item))
                    .map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                </ul>
              )}
              {action.type === 'wait' && (
                <>
                  {action.missingPublications.length > 0 && (
                    <section>
                      <h2>Thông tin đang chờ</h2>
                      <ul>
                        {action.missingPublications.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {action.preparation && (
                    <section className="wizard-prose">
                      <h2>{action.preparation.title}</h2>
                      {action.preparation.paragraphs.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </section>
                  )}
                </>
              )}
              <AdmissionReferences
                references={
                  action.type === 'wait' && action.preparation
                    ? [
                        ...new Map(
                          [...action.references, ...action.preparation.references].map(
                            (reference) => [
                              reference.documentId + reference.sourceSha256 + reference.locator,
                              reference,
                            ]
                          )
                        ).values(),
                      ]
                    : action.references
                }
              />
            </>
          )}
        </div>
      )}
      {stage === 'flow' && !busy && (
        <WizardAdvice
          context={context}
          answers={admissionWireAnswers(answers)}
          answerLabels={fieldLabels}
          sourceIssueIds={
            action?.type === 'wait' || action?.type === 'handoff'
              ? action.sourceIssues
              : action?.type === 'result'
                ? action.options.flatMap((option) => option.sourceIssues)
                : []
          }
          onReceived={() => setDirty(false)}
        />
      )}
    </main>
  );
}
